import type { ScreenerData } from "epsm-api-types/models";
import { useMemo } from "react";
import { formatValueNumber } from "@/components/widgets/helpers/FormattingHelpers";
import {
	componentClassNames,
	grayGradientClassName,
} from "@/components/widgets/Quote/config";
import {
	formatOutPerfSpy,
	formatPositiveRMRSlope,
	formatPriceToEma,
} from "@/components/widgets/Quote/lib/helpers/quoteValueFormatters";
import { QuoteRow } from "@/components/widgets/Quote/ui/components";
import { valueColor } from "@/components/widgets/Revision/utils/RevisionTableUtils";
import { cn } from "@/lib/utils";
import { formatPositiveNumberValue } from "../../../lib/helpers/formatPositiveNumberValue";
import { getGradientForDifferentMetrics } from "../../../lib/helpers/getGradientForDifferentMetrics";
import { getGradientForEmAndRs } from "../../../lib/helpers/getGradientForEmAndRs";
import { useScreenerColumns } from "../../../lib/hooks/useScreenerColumns";

export const EpsmTable = ({
	screenerData,
}: {
	screenerData: ScreenerData | null;
}) => {
	const {
		em_score,
		rs_abs_chg_nasdaq,
		rss_abs_chg_nasdaq,
		trend_indicator_final_status,
		powerday_trend_status,
		volatility_strength_status,
		seq_outperforming_days_spy,
		seq_outperforming_days_sector,
		composite_score,
		price_momentum_signal,
		composite_10d_slope,
		cross_moving_average,
	} = screenerData ?? {};

	const { getColumnValueAndClassName } = useScreenerColumns();

	const { value: trendValue, className: trendClassName } =
		getColumnValueAndClassName(
			"trend_indicator_final_status",
			trend_indicator_final_status,
		);
	const { value: powerdayValue, className: powerdayClassName } =
		getColumnValueAndClassName("powerday_trend_status", powerday_trend_status);
	const { value: volatilityValue, className: volatilityClassName } =
		getColumnValueAndClassName(
			"volatility_strength_status",
			volatility_strength_status,
		);
	const { value: emScoreValue, className: emScoreClassName } =
		getColumnValueAndClassName("em_score", em_score);
	const {
		value: priceMomentumSignalValue,
		className: priceMomentumSignalClassName,
	} = getColumnValueAndClassName(
		"price_momentum_signal",
		price_momentum_signal,
	);
	const { value: compositeScoreValue, className: compositeScoreClassName } =
		getColumnValueAndClassName("composite_score", composite_score);

	const hasAny = useMemo(
		() =>
			em_score != null ||
			rs_abs_chg_nasdaq?.ed != null ||
			rss_abs_chg_nasdaq?.ed != null ||
			trend_indicator_final_status != null ||
			powerday_trend_status != null ||
			volatility_strength_status != null ||
			seq_outperforming_days_spy != null ||
			seq_outperforming_days_sector != null ||
			composite_score != null ||
			composite_10d_slope != null ||
			cross_moving_average?.ema20?.ema50 != null ||
			price_momentum_signal != null,

		[
			em_score,
			rs_abs_chg_nasdaq?.ed,
			rss_abs_chg_nasdaq?.ed,
			trend_indicator_final_status,
			powerday_trend_status,
			volatility_strength_status,
			seq_outperforming_days_spy,
			seq_outperforming_days_sector,
			composite_score,
			composite_10d_slope,
			cross_moving_average?.ema20?.ema50,
			price_momentum_signal,
		],
	);

	const gradientForEmAndRs = useMemo(
		() => getGradientForEmAndRs(screenerData),
		[screenerData],
	);
	const gradientForDifferentMetrics = useMemo(
		() => getGradientForDifferentMetrics(screenerData),
		[screenerData],
	);

	if (!hasAny) return null;

	const hasEm = emScoreValue != null;
	const hasRs = rs_abs_chg_nasdaq?.ed != null || rss_abs_chg_nasdaq?.ed != null;
	const showGreenOrRedGradient = hasEm && hasRs;

	const missingMessage =
		!hasEm && !hasRs
			? "No Earnings and Revision Data available."
			: !hasEm
				? "No Earnings Data or estimates available."
				: !hasRs
					? "No Revision Data or estimates available."
					: null;

	return (
		<div
			className={cn(
				componentClassNames,
				"flex flex-wrap justify-between gap-4",
			)}
			data-testid="epsm-table"
		>
			<div className="flex-1">
				<div
					className={cn(
						showGreenOrRedGradient ? gradientForEmAndRs : grayGradientClassName,
						"h-7 mb-2",
					)}
				/>

				{hasEm && (
					<QuoteRow
						left="EM"
						right={formatValueNumber(emScoreValue)}
						className={`${emScoreClassName} font-bold`}
					/>
				)}
				{rs_abs_chg_nasdaq?.ed != null && (
					<QuoteRow
						left="EPS RS"
						right={formatPositiveNumberValue(rs_abs_chg_nasdaq.ed)}
						className={`${valueColor(rs_abs_chg_nasdaq.ed)} font-bold`}
					/>
				)}
				{rss_abs_chg_nasdaq?.ed != null && (
					<QuoteRow
						left="Sales RS"
						right={formatPositiveNumberValue(rss_abs_chg_nasdaq.ed)}
						className={`${valueColor(rss_abs_chg_nasdaq.ed)} font-bold`}
					/>
				)}

				{missingMessage && <p className="text-light-gray">{missingMessage}</p>}
			</div>

			{(trendValue != null ||
				price_momentum_signal != null ||
				composite_10d_slope != null ||
				composite_score != null ||
				cross_moving_average?.ema20?.ema50 != null ||
				powerdayValue != null ||
				volatilityValue != null ||
				seq_outperforming_days_spy != null ||
				seq_outperforming_days_sector != null) && (
				<div className="flex-1">
					<div className={cn(gradientForDifferentMetrics, "h-7 mb-2")} />
					{trendValue != null && (
						<QuoteRow
							left="Trend"
							right={trendValue}
							className={`${trendClassName} font-bold`}
						/>
					)}
					{price_momentum_signal != null && (
						<QuoteRow
							left="PrMom."
							right={priceMomentumSignalValue}
							className={`${priceMomentumSignalClassName} font-bold`}
						/>
					)}
					{composite_10d_slope != null && (
						<QuoteRow
							left="RMR Slope"
							right={formatPositiveRMRSlope(composite_10d_slope)}
							className={`${valueColor(composite_10d_slope)} font-bold`}
						/>
					)}
					{composite_score != null && (
						<QuoteRow
							left="RMR"
							right={formatValueNumber(compositeScoreValue)}
							className={`${compositeScoreClassName} font-bold`}
						/>
					)}
					{cross_moving_average?.ema20?.ema50 != null && (
						<QuoteRow
							left="EMA 20/50"
							right={formatPriceToEma(cross_moving_average.ema20.ema50)}
							className={`${valueColor(cross_moving_average.ema20.ema50, true)} font-bold`}
						/>
					)}
					{powerdayValue != null && (
						<QuoteRow
							left="Powerdays"
							right={powerdayValue}
							className={`${powerdayClassName} font-bold`}
						/>
					)}
					{volatilityValue != null && (
						<QuoteRow
							left="Volatility"
							right={volatilityValue}
							className={`${volatilityClassName} font-bold`}
						/>
					)}
					{seq_outperforming_days_spy != null && (
						<QuoteRow
							left="OutPerf SPY"
							right={
								<span className="font-bold">
									<span
										className={valueColor(seq_outperforming_days_spy)}
										data-testid="quote-outperf-spy"
									>
										{formatOutPerfSpy(seq_outperforming_days_spy)}
									</span>{" "}
									<span>for {seq_outperforming_days_spy} d</span>
								</span>
							}
						/>
					)}
					{seq_outperforming_days_sector != null && (
						<QuoteRow
							left="OutPerf Sect."
							right={
								<span className="font-bold">
									<span
										className={valueColor(seq_outperforming_days_sector)}
										data-testid="quote-outperf-sector"
									>
										{formatOutPerfSpy(seq_outperforming_days_sector)}
									</span>{" "}
									<span>for {seq_outperforming_days_sector} d</span>
								</span>
							}
						/>
					)}
				</div>
			)}
		</div>
	);
};
